import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  organizationLimitsApi,
  userLimitsApi,
  limitCheckApi,
  creditPoolApi,
  notificationsApi,
  enforcementApi,
  overrideApi,
  handleLimitError,
  OrganizationLimitsData,
  UserLimitsData,
  CreditCheckResponse,
  FeatureAccessResponse,
  CreditPoolStatus,
  LimitNotification,
  EnforcementHistoryItem,
  LimitOverrideData,
  TopupData,
} from '../../services/limitsApi';
import {
  OrganizationLimits,
  UserLimits,
  CreditUsageInfo,
  CreditPoolInfo,
  LimitCheckResult,
  FeatureAccessResult,
  LimitOverride,
  CreditTopup,
  LimitStats,
  UserLimitSummary,
  OrganizationLimitSummary,
  FeatureType,
} from '../../types/limits';

interface LimitsState {
  // Organization limits
  organizationLimits: Record<number, OrganizationLimits>;
  organizationLimitSummaries: OrganizationLimitSummary[];
  
  // User limits
  userLimits: Record<string, UserLimits>; // key: `${userId}-${orgId}`
  userLimitSummaries: UserLimitSummary[];
  
  // Credit usage and pools
  creditUsage: Record<string, CreditUsageInfo>; // key: `${userId}-${orgId}`
  creditPools: Record<number, CreditPoolInfo>; // key: orgId
  
  // Real-time checks (cached)
  limitChecks: Record<string, LimitCheckResult & { timestamp: number }>;
  featureAccess: Record<string, FeatureAccessResult & { timestamp: number }>;
  
  // Notifications
  notifications: LimitNotification[];
  unreadNotificationCount: number;
  
  // History and overrides
  enforcementHistory: EnforcementHistoryItem[];
  activeOverrides: LimitOverride[];
  recentTopups: CreditTopup[];
  
  // Stats
  limitStats: LimitStats | null;
  
  // UI state
  loading: {
    orgLimits: boolean;
    userLimits: boolean;
    creditChecks: boolean;
    notifications: boolean;
    enforcement: boolean;
    overrides: boolean;
    stats: boolean;
  };
  
  error: string | null;
  lastUpdated: string | null;
}

const initialState: LimitsState = {
  organizationLimits: {},
  organizationLimitSummaries: [],
  userLimits: {},
  userLimitSummaries: [],
  creditUsage: {},
  creditPools: {},
  limitChecks: {},
  featureAccess: {},
  notifications: [],
  unreadNotificationCount: 0,
  enforcementHistory: [],
  activeOverrides: [],
  recentTopups: [],
  limitStats: null,
  loading: {
    orgLimits: false,
    userLimits: false,
    creditChecks: false,
    notifications: false,
    enforcement: false,
    overrides: false,
    stats: false,
  },
  error: null,
  lastUpdated: null,
};

// Organization Limits Thunks
export const fetchOrganizationLimits = createAsyncThunk(
  'limits/fetchOrganizationLimits',
  async (orgId: number, { rejectWithValue }) => {
    try {
      const response = await organizationLimitsApi.getOrgLimits(orgId);
      return { orgId, limits: response };
    } catch (error: any) {
      return rejectWithValue(handleLimitError(error, 'fetch organization limits'));
    }
  }
);

export const createOrganizationLimits = createAsyncThunk(
  'limits/createOrganizationLimits',
  async ({ orgId, limitsData }: { orgId: number; limitsData: OrganizationLimitsData }, { rejectWithValue }) => {
    try {
      const response = await organizationLimitsApi.createOrgLimits(orgId, limitsData);
      return { orgId, limits: response };
    } catch (error: any) {
      return rejectWithValue(handleLimitError(error, 'create organization limits'));
    }
  }
);

export const updateOrganizationLimits = createAsyncThunk(
  'limits/updateOrganizationLimits',
  async ({ orgId, limitsData }: { orgId: number; limitsData: Partial<OrganizationLimitsData> }, { rejectWithValue }) => {
    try {
      const response = await organizationLimitsApi.updateOrgLimits(orgId, limitsData);
      return { orgId, limits: response };
    } catch (error: any) {
      return rejectWithValue(handleLimitError(error, 'update organization limits'));
    }
  }
);

// User Limits Thunks
export const fetchUserLimits = createAsyncThunk(
  'limits/fetchUserLimits',
  async ({ userId, orgId }: { userId: number; orgId: number }, { rejectWithValue }) => {
    try {
      const response = await userLimitsApi.getUserLimits(userId, orgId);
      return { userId, orgId, limits: response };
    } catch (error: any) {
      return rejectWithValue(handleLimitError(error, 'fetch user limits'));
    }
  }
);

export const createUserLimits = createAsyncThunk(
  'limits/createUserLimits',
  async ({ userId, orgId, limitsData }: { userId: number; orgId: number; limitsData: UserLimitsData }, { rejectWithValue }) => {
    try {
      const response = await userLimitsApi.createUserLimits(userId, orgId, limitsData);
      return { userId, orgId, limits: response };
    } catch (error: any) {
      return rejectWithValue(handleLimitError(error, 'create user limits'));
    }
  }
);

export const updateUserLimits = createAsyncThunk(
  'limits/updateUserLimits',
  async ({ userId, orgId, limitsData }: { userId: number; orgId: number; limitsData: Partial<UserLimitsData> }, { rejectWithValue }) => {
    try {
      const response = await userLimitsApi.updateUserLimits(userId, orgId, limitsData);
      return { userId, orgId, limits: response };
    } catch (error: any) {
      return rejectWithValue(handleLimitError(error, 'update user limits'));
    }
  }
);

// Credit Check Thunks
export const checkUserCredit = createAsyncThunk(
  'limits/checkUserCredit',
  async ({ userId, orgId, creditAmount }: { userId: number; orgId: number; creditAmount: number }, { rejectWithValue }) => {
    try {
      const response = await limitCheckApi.checkUserCredit(userId, orgId, creditAmount);
      return { userId, orgId, creditAmount, result: response };
    } catch (error: any) {
      return rejectWithValue(handleLimitError(error, 'check user credit'));
    }
  }
);

export const checkFeatureAccess = createAsyncThunk(
  'limits/checkFeatureAccess',
  async ({ userId, orgId, feature }: { userId: number; orgId: number; feature: FeatureType }, { rejectWithValue }) => {
    try {
      const response = await limitCheckApi.checkFeatureAccess(userId, orgId, feature);
      return { userId, orgId, feature, result: response };
    } catch (error: any) {
      return rejectWithValue(handleLimitError(error, 'check feature access'));
    }
  }
);

// Credit Pool Thunks
export const fetchCreditPoolStatus = createAsyncThunk(
  'limits/fetchCreditPoolStatus',
  async (orgId: number, { rejectWithValue }) => {
    try {
      const response = await creditPoolApi.getCreditPoolStatus(orgId);
      return { orgId, pool: response };
    } catch (error: any) {
      return rejectWithValue(handleLimitError(error, 'fetch credit pool status'));
    }
  }
);

export const createCreditTopup = createAsyncThunk(
  'limits/createCreditTopup',
  async ({ orgId, topupData }: { orgId: number; topupData: TopupData }, { rejectWithValue }) => {
    try {
      const response = await creditPoolApi.createTopup(orgId, topupData);
      return { orgId, topup: response };
    } catch (error: any) {
      return rejectWithValue(handleLimitError(error, 'create credit topup'));
    }
  }
);

// Notifications Thunks
export const fetchNotifications = createAsyncThunk(
  'limits/fetchNotifications',
  async ({ orgId, unreadOnly = false }: { orgId: number; unreadOnly?: boolean }, { rejectWithValue }) => {
    try {
      const response = await notificationsApi.getNotifications(orgId, unreadOnly);
      return { notifications: response, unreadOnly };
    } catch (error: any) {
      return rejectWithValue(handleLimitError(error, 'fetch notifications'));
    }
  }
);

export const markNotificationRead = createAsyncThunk(
  'limits/markNotificationRead',
  async (notificationId: number, { rejectWithValue }) => {
    try {
      await notificationsApi.markNotificationRead(notificationId);
      return notificationId;
    } catch (error: any) {
      return rejectWithValue(handleLimitError(error, 'mark notification as read'));
    }
  }
);

export const markAllNotificationsRead = createAsyncThunk(
  'limits/markAllNotificationsRead',
  async (orgId: number, { rejectWithValue }) => {
    try {
      await notificationsApi.markAllNotificationsRead(orgId);
      return orgId;
    } catch (error: any) {
      return rejectWithValue(handleLimitError(error, 'mark all notifications as read'));
    }
  }
);

// Enforcement History Thunks
export const fetchEnforcementHistory = createAsyncThunk(
  'limits/fetchEnforcementHistory',
  async ({ orgId, userId, limit = 100 }: { orgId?: number; userId?: number; limit?: number }, { rejectWithValue }) => {
    try {
      const response = await enforcementApi.getEnforcementHistory(orgId, userId, limit);
      return response;
    } catch (error: any) {
      return rejectWithValue(handleLimitError(error, 'fetch enforcement history'));
    }
  }
);

// Override Thunks
export const applyLimitOverride = createAsyncThunk(
  'limits/applyLimitOverride',
  async (overrideData: LimitOverrideData, { rejectWithValue }) => {
    try {
      const response = await overrideApi.applyLimitOverride(overrideData);
      return response;
    } catch (error: any) {
      return rejectWithValue(handleLimitError(error, 'apply limit override'));
    }
  }
);

export const fetchActiveOverrides = createAsyncThunk(
  'limits/fetchActiveOverrides',
  async (orgId: number | undefined, { rejectWithValue }) => {
    try {
      const response = await overrideApi.getActiveOverrides(orgId);
      return response;
    } catch (error: any) {
      return rejectWithValue(handleLimitError(error, 'fetch active overrides'));
    }
  }
);

export const removeOverride = createAsyncThunk(
  'limits/removeOverride',
  async (overrideId: number, { rejectWithValue }) => {
    try {
      await overrideApi.removeOverride(overrideId);
      return overrideId;
    } catch (error: any) {
      return rejectWithValue(handleLimitError(error, 'remove override'));
    }
  }
);

const limitsSlice = createSlice({
  name: 'limits',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    
    clearCache: (state) => {
      state.limitChecks = {};
      state.featureAccess = {};
    },
    
    updateCreditUsage: (state, action: PayloadAction<{ userId: number; orgId: number; usage: CreditUsageInfo }>) => {
      const { userId, orgId, usage } = action.payload;
      const key = `${userId}-${orgId}`;
      state.creditUsage[key] = usage;
      state.lastUpdated = new Date().toISOString();
    },
    
    addNotification: (state, action: PayloadAction<LimitNotification>) => {
      state.notifications.unshift(action.payload);
      if (!action.payload.read) {
        state.unreadNotificationCount += 1;
      }
    },
    
    updateNotificationCount: (state, action: PayloadAction<number>) => {
      state.unreadNotificationCount = action.payload;
    },
    
    // Cache management
    setCreditCheckCache: (state, action: PayloadAction<{ key: string; result: LimitCheckResult }>) => {
      const { key, result } = action.payload;
      state.limitChecks[key] = {
        ...result,
        timestamp: Date.now(),
      };
    },
    
    setFeatureAccessCache: (state, action: PayloadAction<{ key: string; result: FeatureAccessResult }>) => {
      const { key, result } = action.payload;
      state.featureAccess[key] = {
        ...result,
        timestamp: Date.now(),
      };
    },
    
    // Clean expired cache entries
    cleanExpiredCache: (state) => {
      const now = Date.now();
      const cacheExpiry = 5 * 60 * 1000; // 5 minutes
      
      // Clean limit checks cache
      Object.keys(state.limitChecks).forEach(key => {
        if (now - state.limitChecks[key].timestamp > cacheExpiry) {
          delete state.limitChecks[key];
        }
      });
      
      // Clean feature access cache
      Object.keys(state.featureAccess).forEach(key => {
        if (now - state.featureAccess[key].timestamp > cacheExpiry) {
          delete state.featureAccess[key];
        }
      });
    },
  },
  
  extraReducers: (builder) => {
    builder
      // Organization Limits
      .addCase(fetchOrganizationLimits.pending, (state) => {
        state.loading.orgLimits = true;
        state.error = null;
      })
      .addCase(fetchOrganizationLimits.fulfilled, (state, action) => {
        state.loading.orgLimits = false;
        state.organizationLimits[action.payload.orgId] = action.payload.limits;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchOrganizationLimits.rejected, (state, action) => {
        state.loading.orgLimits = false;
        state.error = action.payload as string;
      })
      
      .addCase(createOrganizationLimits.pending, (state) => {
        state.loading.orgLimits = true;
        state.error = null;
      })
      .addCase(createOrganizationLimits.fulfilled, (state, action) => {
        state.loading.orgLimits = false;
        state.organizationLimits[action.payload.orgId] = action.payload.limits;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(createOrganizationLimits.rejected, (state, action) => {
        state.loading.orgLimits = false;
        state.error = action.payload as string;
      })
      
      .addCase(updateOrganizationLimits.fulfilled, (state, action) => {
        state.organizationLimits[action.payload.orgId] = action.payload.limits;
        state.lastUpdated = new Date().toISOString();
      })
      
      // User Limits
      .addCase(fetchUserLimits.pending, (state) => {
        state.loading.userLimits = true;
        state.error = null;
      })
      .addCase(fetchUserLimits.fulfilled, (state, action) => {
        state.loading.userLimits = false;
        const key = `${action.payload.userId}-${action.payload.orgId}`;
        state.userLimits[key] = action.payload.limits;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchUserLimits.rejected, (state, action) => {
        state.loading.userLimits = false;
        state.error = action.payload as string;
      })
      
      .addCase(createUserLimits.fulfilled, (state, action) => {
        const key = `${action.payload.userId}-${action.payload.orgId}`;
        state.userLimits[key] = action.payload.limits;
        state.lastUpdated = new Date().toISOString();
      })
      
      .addCase(updateUserLimits.fulfilled, (state, action) => {
        const key = `${action.payload.userId}-${action.payload.orgId}`;
        state.userLimits[key] = action.payload.limits;
        state.lastUpdated = new Date().toISOString();
      })
      
      // Credit Checks
      .addCase(checkUserCredit.pending, (state) => {
        state.loading.creditChecks = true;
      })
      .addCase(checkUserCredit.fulfilled, (state, action) => {
        state.loading.creditChecks = false;
        const { userId, orgId, result } = action.payload;
        const key = `credit-${userId}-${orgId}`;
        state.limitChecks[key] = {
          ...result,
          timestamp: Date.now(),
        };
      })
      .addCase(checkUserCredit.rejected, (state, action) => {
        state.loading.creditChecks = false;
        state.error = action.payload as string;
      })
      
      .addCase(checkFeatureAccess.fulfilled, (state, action) => {
        const { userId, orgId, feature, result } = action.payload;
        const key = `feature-${feature}-${userId}-${orgId}`;
        state.featureAccess[key] = {
          ...result,
          feature,
          timestamp: Date.now(),
        };
      })
      
      // Credit Pool
      .addCase(fetchCreditPoolStatus.fulfilled, (state, action) => {
        const { orgId, pool } = action.payload;
        state.creditPools[orgId] = pool as CreditPoolInfo;
        state.lastUpdated = new Date().toISOString();
      })
      
      .addCase(createCreditTopup.fulfilled, (state, action) => {
        state.recentTopups.unshift(action.payload.topup);
        // Keep only last 10 topups
        if (state.recentTopups.length > 10) {
          state.recentTopups = state.recentTopups.slice(0, 10);
        }
      })
      
      // Notifications
      .addCase(fetchNotifications.pending, (state) => {
        state.loading.notifications = true;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading.notifications = false;
        state.notifications = action.payload.notifications;
        state.unreadNotificationCount = action.payload.notifications.filter(n => !n.read).length;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading.notifications = false;
        state.error = action.payload as string;
      })
      
      .addCase(markNotificationRead.fulfilled, (state, action) => {
        const notificationId = action.payload;
        const notification = state.notifications.find(n => n.id === notificationId);
        if (notification && !notification.read) {
          notification.read = true;
          state.unreadNotificationCount = Math.max(0, state.unreadNotificationCount - 1);
        }
      })
      
      .addCase(markAllNotificationsRead.fulfilled, (state) => {
        state.notifications.forEach(notification => {
          notification.read = true;
        });
        state.unreadNotificationCount = 0;
      })
      
      // Enforcement History
      .addCase(fetchEnforcementHistory.pending, (state) => {
        state.loading.enforcement = true;
      })
      .addCase(fetchEnforcementHistory.fulfilled, (state, action) => {
        state.loading.enforcement = false;
        state.enforcementHistory = action.payload;
      })
      .addCase(fetchEnforcementHistory.rejected, (state, action) => {
        state.loading.enforcement = false;
        state.error = action.payload as string;
      })
      
      // Overrides
      .addCase(fetchActiveOverrides.pending, (state) => {
        state.loading.overrides = true;
      })
      .addCase(fetchActiveOverrides.fulfilled, (state, action) => {
        state.loading.overrides = false;
        state.activeOverrides = action.payload;
      })
      .addCase(fetchActiveOverrides.rejected, (state, action) => {
        state.loading.overrides = false;
        state.error = action.payload as string;
      })
      
      .addCase(applyLimitOverride.fulfilled, (state, action) => {
        state.activeOverrides.push(action.payload);
      })
      
      .addCase(removeOverride.fulfilled, (state, action) => {
        const overrideId = action.payload;
        state.activeOverrides = state.activeOverrides.filter(override => override.id !== overrideId);
      });
  },
});

export const {
  clearError,
  clearCache,
  updateCreditUsage,
  addNotification,
  updateNotificationCount,
  setCreditCheckCache,
  setFeatureAccessCache,
  cleanExpiredCache,
} = limitsSlice.actions;

export default limitsSlice.reducer;
